import ComponentWithSocket from '../../../lib/component';
import { Component, Prop } from 'vue-property-decorator';
import { BvModalEvent } from 'bootstrap-vue';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';
import { saveAs } from 'file-saver';
import { saveXlsx, XlsxCellInput, XlsxSheet } from '../../../utils/xlsx';
import {
    contractLabel,
    groupSelectedCount,
    isGroupFullySelected,
    lotsLabel,
    safeFileNamePart,
    selectedLotCount,
    setLotsSelected,
    totalLotCount,
    truncate2,
} from '../../../utils/export';
import {
    CONTRACT_PENDING,
    contractTransferBlockReason,
    depositorDocxData,
    depositorName,
    isCompany,
} from '../../../utils/depositor';
import moment from 'moment';
import 'moment/locale/ro';
moment.locale('ro');

// Coloanele tabelelor din fiecare card de deponent. Scrise inline in template,
// erau un array nou la fiecare randare, iar `b-table` isi refacea toate
// randurile din toate cardurile la orice schimbare din tab.
const LOT_FIELDS = [
    { key: 'select', label: '' },
    { key: 'foto', label: 'Foto' },
    { key: 'numar', label: 'Nr.' },
    { key: 'nume', label: 'Nume' },
    { key: 'contract', label: 'Contract' },
    { key: 'adjudecat', label: 'Adjudecat' },
    { key: 'status_incasare', label: 'Status încasare' },
    { key: 'pret_inceput', label: 'Preț început' },
    { key: 'pret_adjudecare', label: 'Preț adjudecare' },
    { key: 'comision_pct', label: 'Comision %' },
    { key: 'comision', label: 'Comision' },
    { key: 'suma_plata', label: 'Sumă de plată' },
    { key: 'platit_deponent', label: 'Plătit deponent' },
];

const NOTE_FIELDS = [
    { key: 'createdAt', label: 'Data adăugării' },
    { key: 'code', label: 'Cod' },
    { key: 'description', label: 'Descriere' },
    { key: 'createdBy', label: 'User' },
    { key: 'actiuni', label: '' },
];

@Component
export default class DepositorsTab extends ComponentWithSocket {
    depositorName = depositorName;
    isCompany = isCompany;
    contractLabel = contractLabel;
    lotFields = LOT_FIELDS;
    noteFields = NOTE_FIELDS;

    @Prop({ required: true }) auction: any;
    @Prop({ required: true }) auctionId: string;
    @Prop({ required: true }) selectedCurrency: string;

    public data: {
        auctionDepositors: any[];
        updatingPaidStatus: string;
        updatingCommission: string;
    } = {
        auctionDepositors: [],
        updatingPaidStatus: '',
        updatingCommission: '',
    };

    public situatie: {
        isOpen: boolean;
        isGenerating: boolean;
        error: string;
        depositor: any;
        lots: any[];
    } = {
        isOpen: false,
        isGenerating: false,
        error: '',
        depositor: undefined,
        lots: [],
    };

    public excelExport: {
        isOpen: boolean;
        stale: boolean;
        error: string;
        groups: any[];
    } = {
        isOpen: false,
        stale: false,
        error: '',
        groups: [],
    };

    public contractMove: {
        selected: { [lotId: string]: boolean };
        isOpen: boolean;
        isLoadingContracts: boolean;
        isSaving: boolean;
        error: string;
        lotErrors: { [lotId: string]: string };
        result: any;
        owners: any[];
        newOwnerId: string;
        useExisting: boolean;
        target: any;
        contracts: any[];
        lots: any[];
    } = {
        selected: {},
        isOpen: false,
        isLoadingContracts: false,
        isSaving: false,
        error: '',
        lotErrors: {},
        result: undefined,
        owners: [],
        newOwnerId: '',
        useExisting: false,
        target: undefined,
        contracts: [],
        lots: [],
    };

    public commissionDraft: { [lotId: string]: string } = {};

    public depositorNotes: { [depositorId: string]: any[] } = {};
    public addingNote: { [depositorId: string]: boolean } = {};
    public newNote: { [depositorId: string]: { description: string } } = {};

    private paidStatusSaved: { [lotId: string]: boolean } = {};
    private paidStatusRollback: { [lotId: string]: boolean } = {};
    private commissionSaved: { [lotId: string]: number | undefined } = {};
    private commissionRollback: { [lotId: string]: number | undefined } = {};

    constructor() {
        super();

        this.wsListener.onGet(
            'admin.auctions.depositors',
            (success: boolean, data: any) => {
                if (success) {
                    this.data.auctionDepositors = data;
                    this.hydrateLotState(data);

                    // The export modal works off its own snapshot, so the
                    // selection survives this refresh - just flag it as stale.
                    if (this.excelExport.isOpen) {
                        this.excelExport.stale = true;
                    }

                    (data || []).forEach((depositor: any) => {
                        const did = depositor.depositor && depositor.depositor._id;
                        if (!did) return;

                        if (!this.depositorNotes[did]) this.$set(this.depositorNotes, did, []);
                        if (!this.newNote[did]) this.$set(this.newNote, did, { description: '' });
                        if (!this.addingNote[did]) this.$set(this.addingNote, did, false);
                    });

                    this.loadDepositorNotes();
                }
            },
        );

        this.wsListener.onGet(
            'admin.auctions.depositors.notes',
            (success: boolean, data: any) => {
                if (!success || !data) return;

                const notesByDepositor: { [depositorId: string]: any[] } = {};
                Object.keys(this.depositorNotes).forEach((did: string) => {
                    notesByDepositor[did] = [];
                });
                (data.notes || []).forEach((note: any) => {
                    const did = String(note.depositor);
                    (notesByDepositor[did] = notesByDepositor[did] || []).push(note);
                });

                Object.keys(notesByDepositor).forEach((did: string) => {
                    this.$set(this.depositorNotes, did, notesByDepositor[did]);
                });
            },
        );

        this.wsListener.onPut(
            'admin.auctions.depositors.notes',
            (success: boolean, data: any) => {
                if (success && data && data.depositor && data.note) {
                    const did = data.depositor;
                    const notes = this.depositorNotes[did]
                        ? [...this.depositorNotes[did]]
                        : [];
                    notes.unshift(data.note);
                    this.$set(this.depositorNotes, did, notes);
                    this.$set(this.newNote, did, { description: '' });
                } else {
                    alert((data && data.error) || 'A aparut o eroare la adaugarea notei');
                }
                if (data && data.depositor) {
                    this.$set(this.addingNote, data.depositor, false);
                }
            },
        );

        this.wsListener.onDelete(
            'admin.auctions.depositors.notes',
            (success: boolean, data: any) => {
                if (success && data && data.depositor && data.noteId) {
                    const did = data.depositor;
                    const notes = (this.depositorNotes[did] || []).filter(
                        (n: any) => String(n._id) !== String(data.noteId),
                    );
                    this.$set(this.depositorNotes, did, notes);
                } else {
                    alert((data && data.error) || 'A aparut o eroare la stergerea notei');
                }
            },
        );

        this.wsListener.onPut(
            'admin.auctions.depositors.lotPaidStatus',
            (success: boolean, data: any) => {
                this.data.updatingPaidStatus = '';

                const lotId = data && data.lot;
                if (success) {
                    if (lotId) {
                        this.paidStatusSaved[lotId] = !!data.paidToDepositor;
                        delete this.paidStatusRollback[lotId];
                    }
                    return;
                }

                if (lotId && this.paidStatusRollback[lotId] !== undefined) {
                    this.applyLotPaid(lotId, this.paidStatusRollback[lotId]);
                    delete this.paidStatusRollback[lotId];
                }

                alert(
                    (data && data.error) ||
                        'A aparut o eroare la actualizarea statusului platii',
                );
            },
        );

        this.wsListener.onPut(
            'admin.auctions.depositors.lotCommission',
            (success: boolean, data: any) => {
                this.data.updatingCommission = '';

                const lotId = data && data.lot;
                if (success) {
                    if (lotId) {
                        const newPct =
                            data.commissionPercent != undefined
                                ? Number(data.commissionPercent)
                                : undefined;
                        this.commissionSaved[lotId] = newPct;
                        delete this.commissionRollback[lotId];
                    }

                    this.wsListener.sendGet('admin.auctions.depositors', {
                        auction: this.auctionId,
                    });
                    return;
                }

                if (
                    lotId &&
                    Object.prototype.hasOwnProperty.call(
                        this.commissionRollback,
                        lotId,
                    )
                ) {
                    this.applyLotCommission(
                        lotId,
                        this.commissionRollback[lotId],
                    );
                    delete this.commissionRollback[lotId];
                }

                alert(
                    (data && data.error) ||
                        'A aparut o eroare la actualizarea comisionului',
                );
            },
        );

        this.wsListener.onGet(
            'admin.depositors.contracts',
            (success: boolean, data: any) => {
                if (!this.contractMove.isOpen) return;
                this.contractMove.isLoadingContracts = false;

                if (!success) {
                    this.contractMove.error =
                        'Nu am putut încărca contractele nesemnate.';
                    return;
                }

                this.contractMove.contracts = this.targetContractOptions(
                    Array.isArray(data) ? data : [],
                );
            },
        );

        this.wsListener.onPut(
            'admin.auctions.depositors.lotsContract',
            (success: boolean, data: any) => {
                this.contractMove.isSaving = false;

                const lotErrors: { [lotId: string]: string } = {};
                ((data && data.lots) || []).forEach((item: any) => {
                    lotErrors[String(item.lot)] = item.error;
                });
                this.contractMove.lotErrors = lotErrors;

                if (!success) {
                    this.contractMove.error =
                        (data && data.error) ||
                        'Loturile nu au putut fi mutate în contract.';
                    return;
                }

                (data.moved || []).forEach((lotId: string) => {
                    this.$delete(this.contractMove.selected, String(lotId));
                });

                this.contractMove.result = {
                    contract: data.contract,
                    depositorName: this.contractMoveTargetDepositorName,
                    movedCount: (data.moved || []).length,
                    failed: (data.lots || []).length,
                };
                this.contractMove.isOpen = false;

                this.wsListener.sendGet('admin.auctions.depositors', {
                    auction: this.auctionId,
                });
                // Preturile s-au schimbat si pe loturile din licitatie.
                this.$emit('lots-changed');
            },
        );
    }

    mounted() {
        this.wsListener.sendGet('admin.auctions.depositors', {
            auction: this.auctionId,
        });
    }

    beforeDestroy() {
        this.wsListener.destroy();
    }

    get currencyValue(): string {
        return this.selectedCurrency;
    }

    set currencyValue(val: string) {
        this.$emit('update:selectedCurrency', val);
    }

    get currencySymbol() {
        if (this.selectedCurrency == 'EUR') return '€';
        return 'Lei';
    }

    getComissionAmount(amount: number, percent: number) {
        return amount * (percent / 100);
    }

    getNetAmount(amount: number, percent: number) {
        return amount - this.getComissionAmount(amount, percent);
    }

    formatPrice(amount: number) {
        if (this.selectedCurrency == 'EUR') return amount;
        return amount * this.auction.euroRate;
    }

    effectiveCommissionPercent(lot: any, depositor: any): number {
        if (lot && lot.commissionPercent != undefined) {
            return Number(lot.commissionPercent);
        }
        if (lot && lot.depositorCommissionPercent != undefined) {
            return Number(lot.depositorCommissionPercent);
        }
        return Number(depositor && depositor.commissionPercent);
    }

    contractsLabel(depositor: any): string {
        return (depositor.contracts || [])
            .map((c: any) => contractLabel(c) || '-')
            .join(', ');
    }

    lotAdjudecatPrice(lot: any): number {
        if (lot.sold && lot.winnerPrice) {
            return lot.winnerPrice.price;
        }
        return 0;
    }

    lotRowClass(lot: any): string {
        return lot && lot.paidToDepositor ? 'lot-row-paid' : '';
    }

    private hydrateLotState(depositors: any[]) {
        const draft: { [lotId: string]: string } = {};
        (depositors || []).forEach((depositor: any) => {
            (depositor.lots || []).forEach((lot: any) => {
                const id = String(lot._id);
                lot.paidToDepositor = !!lot.paidToDepositor;
                this.paidStatusSaved[id] = lot.paidToDepositor;

                const pct =
                    lot.depositorCommissionPercent != undefined
                        ? Number(lot.depositorCommissionPercent)
                        : undefined;
                this.commissionSaved[id] = pct;
                draft[id] = pct != undefined ? String(pct) : '';
            });
        });
        this.commissionDraft = draft;
    }

    private applyLotPaid(lotId: string, value: boolean) {
        (this.data.auctionDepositors || []).forEach((depositor: any) => {
            (depositor.lots || []).forEach((lot: any) => {
                if (String(lot._id) === String(lotId)) {
                    lot.paidToDepositor = value;
                }
            });
        });
    }

    private applyLotCommission(lotId: string, value: number | undefined) {
        (this.data.auctionDepositors || []).forEach((depositor: any) => {
            (depositor.lots || []).forEach((lot: any) => {
                if (String(lot._id) === String(lotId)) {
                    lot.depositorCommissionPercent = value;
                }
            });
        });
        this.$set(
            this.commissionDraft,
            lotId,
            value != undefined ? String(value) : '',
        );
    }

    updateLotPaidStatus(lot: any, value: any) {
        if (!lot || !lot._id) return;
        const lotId = String(lot._id);
        const next = value === true || value === 'true' || value === 1 || value === '1';

        this.paidStatusRollback[lotId] =
            this.paidStatusSaved[lotId] !== undefined
                ? this.paidStatusSaved[lotId]
                : !next;
        lot.paidToDepositor = next;
        this.data.updatingPaidStatus = lotId;

        this.wsListener.sendPut('admin.auctions.depositors.lotPaidStatus', {
            auction: this.auctionId,
            lot: lotId,
            paidToDepositor: next,
        });
    }

    updateLotCommission(lot: any) {
        if (!lot || !lot._id) return;
        const lotId = String(lot._id);
        const raw = this.commissionDraft[lotId];

        let parsed: number | undefined = undefined;
        if (raw !== undefined && String(raw).trim() !== '') {
            const num = Number(raw);
            if (!Number.isFinite(num) || num < 0 || num > 100) {
                alert('Procentul trebuie sa fie un numar intre 0 si 100');
                const prev = this.commissionSaved[lotId];
                this.$set(
                    this.commissionDraft,
                    lotId,
                    prev != undefined ? String(prev) : '',
                );
                return;
            }
            parsed = num;
        }

        const prev = this.commissionSaved[lotId];
        if (
            (prev == undefined && parsed == undefined) ||
            (prev != undefined && parsed != undefined && Number(prev) === parsed)
        ) {
            return;
        }

        this.commissionRollback[lotId] = prev;
        lot.depositorCommissionPercent = parsed;
        this.data.updatingCommission = lotId;

        this.wsListener.sendPut('admin.auctions.depositors.lotCommission', {
            auction: this.auctionId,
            lot: lotId,
            commissionPercent: parsed,
        });
    }

    lotTransferBlockReason(lot: any): string {
        return contractTransferBlockReason(lot, this.auction);
    }

    isLotSelectedForContract(lot: any): boolean {
        return !!this.contractMove.selected[String(lot._id)];
    }

    setLotSelectedForContract(lot: any, value: boolean) {
        const lotId = String(lot._id);
        if (value && !this.lotTransferBlockReason(lot)) {
            this.$set(this.contractMove.selected, lotId, true);
        } else {
            this.$delete(this.contractMove.selected, lotId);
        }
    }

    transferableLots(depositor: any): any[] {
        return (depositor.lots || []).filter(
            (lot: any) => !this.lotTransferBlockReason(lot),
        );
    }

    selectedLotsForContract(depositor: any): any[] {
        return this.transferableLots(depositor).filter((lot: any) =>
            this.isLotSelectedForContract(lot),
        );
    }

    allTransferableSelected(depositor: any): boolean {
        const lots = this.transferableLots(depositor);
        return (
            lots.length > 0 &&
            lots.every((lot: any) => this.isLotSelectedForContract(lot))
        );
    }

    toggleAllForContract(depositor: any) {
        const next = !this.allTransferableSelected(depositor);
        this.transferableLots(depositor).forEach((lot: any) =>
            this.setLotSelectedForContract(lot, next),
        );
    }

    /** Loturile bifate din tot tabul, pentru butonul de sus. */
    get selectedLotsCountForContract(): number {
        return (this.data.auctionDepositors || []).reduce(
            (total: number, depositor: any) =>
                total + this.selectedLotsForContract(depositor).length,
            0,
        );
    }

    get hasTransferableLots(): boolean {
        return (this.data.auctionDepositors || []).some(
            (depositor: any) => this.transferableLots(depositor).length > 0,
        );
    }

    /**
     * Optiunile pentru `v-select`: toate contractele nesemnate, fara cele din
     * care pleaca loturile, cu ale deponentilor loturilor primele.
     */
    private targetContractOptions(contracts: any[]): any[] {
        const ownerIds = this.contractMove.owners.map((owner: any) =>
            String(owner._id),
        );
        const currentIds = (this.contractMove.lots || []).map(
            (lot: any) => lot.contractId,
        );
        const isOwn = (option: any) =>
            ownerIds.indexOf(option.depositorId) !== -1;

        return contracts
            .filter(
                (contract: any) =>
                    contract.status === CONTRACT_PENDING &&
                    currentIds.indexOf(String(contract._id)) === -1,
            )
            .map((contract: any) => {
                const name = depositorName(contract.depositor);
                return {
                    label: `${contractLabel(contract) || '-'} — ${name ||
                        '(fără deponent)'}`,
                    value: String(contract._id),
                    depositorId: String(
                        contract.depositor && contract.depositor._id,
                    ),
                    depositorName: name,
                };
            })
            .sort((a: any, b: any) => Number(isOwn(b)) - Number(isOwn(a)));
    }

    /** Deponentul contractului in care ajung loturile (cel ales sau cel nou). */
    private get contractMoveTargetDepositorId(): string {
        const target = this.contractMove.useExisting && this.contractMove.target;
        return target ? target.depositorId : this.contractMove.newOwnerId;
    }

    get contractMoveTargetDepositorName(): string {
        const target = this.contractMove.useExisting && this.contractMove.target;
        if (target) return target.depositorName;

        const owner = this.contractMove.owners.find(
            (o: any) => String(o._id) === this.contractMove.newOwnerId,
        );
        return depositorName(owner);
    }

    /** Cate loturi trec la alt deponent decat al lor. */
    get contractMoveDepositorChanges(): number {
        const targetId = this.contractMoveTargetDepositorId;
        if (!targetId) return 0;
        return this.contractMove.lots.filter(
            (lot: any) => lot.depositorId !== targetId,
        ).length;
    }

    get contractMoveFields(): any[] {
        const fields: any[] = [
            { key: 'foto', label: 'Foto' },
            { key: 'position', label: 'Lot' },
            { key: 'name', label: 'Denumire' },
            { key: 'contract', label: 'Contract actual' },
            { key: 'startingPrice', label: 'Preț pornire (€)' },
            { key: 'estimateMin', label: 'Estimare min (€)' },
            { key: 'estimateMax', label: 'Estimare max (€)' },
            { key: 'actions', label: '' },
        ];
        if (this.contractMove.owners.length > 1) {
            fields.splice(3, 0, { key: 'depositor', label: 'Deponent' });
        }
        return fields;
    }

    /**
     * Fara argument: loturile bifate din tot tabul, de la oricati deponenti
     * (ex. sot si sotie intr-un singur contract). Cu deponent: doar ale lui.
     */
    openContractMove(depositor?: any) {
        const groups = depositor ? [depositor] : this.data.auctionDepositors;
        const selected: { lot: any; owner: any }[] = [];
        const owners: any[] = [];

        (groups || []).forEach((group: any) => {
            const lots = this.selectedLotsForContract(group);
            if (!lots.length) return;
            owners.push(group.depositor);
            lots.forEach((lot: any) =>
                selected.push({ lot, owner: group.depositor }),
            );
        });
        if (!selected.length) return;

        const amount = (value: any) =>
            value != undefined && value !== '' ? String(value) : '';
        const lotValue = (lot: any, field: string) =>
            lot[field] != undefined
                ? lot[field]
                : lot.depositorLot && lot.depositorLot[field];

        this.contractMove.owners = owners;
        this.contractMove.newOwnerId = String(owners[0]._id);
        this.contractMove.useExisting = false;
        this.contractMove.target = undefined;
        this.contractMove.contracts = [];
        this.contractMove.error = '';
        this.contractMove.lotErrors = {};
        this.contractMove.isSaving = false;
        this.contractMove.lots = selected.map(({ lot, owner }) => ({
            id: String(lot._id),
            depositorId: String(owner._id),
            depositorName: depositorName(owner),
            position: lot.position || 0,
            name: lot.name ? String(lot.name) : '',
            author_name:
                lot.author && lot.author.name ? String(lot.author.name) : '',
            thumbnail:
                lot.imagesData && lot.imagesData.length
                    ? lot.imagesData[0].thumbnail
                    : '',
            contractId: String(lot.depositorContract._id),
            contract: contractLabel(lot.depositorContract),
            startingPrice: amount(lotValue(lot, 'startingPrice')),
            estimateMin: amount(lotValue(lot, 'estimateMin')),
            estimateMax: amount(lotValue(lot, 'estimateMax')),
        }));
        this.contractMove.isLoadingContracts = true;
        this.contractMove.isOpen = true;

        this.wsListener.sendGet('admin.depositors.contracts', {
            status: CONTRACT_PENDING,
        });
    }

    /** Scoate un lot din mutare, ex. unul refuzat de server. */
    removeFromContractMove(lotId: string) {
        this.contractMove.lots = this.contractMove.lots.filter(
            (lot: any) => lot.id !== lotId,
        );
        this.$delete(this.contractMove.selected, lotId);
        this.$delete(this.contractMove.lotErrors, lotId);

        if (!this.contractMove.lots.length) {
            this.contractMove.isOpen = false;
        }
    }

    doContractMove(bvModalEvt?: BvModalEvent) {
        if (bvModalEvt) bvModalEvt.preventDefault();

        const lots = this.contractMove.lots || [];
        if (!lots.length || this.contractMove.isSaving) return;

        const missingPrice = lots.find(
            (lot: any) => !(Number(lot.startingPrice) > 0),
        );
        if (missingPrice) {
            this.contractMove.error = `Completează prețul de pornire pentru LOT ${missingPrice.position}.`;
            return;
        }

        const target = this.contractMove.useExisting
            ? this.contractMove.target
            : undefined;
        if (this.contractMove.useExisting && !target) {
            this.contractMove.error = 'Alege contractul nesemnat.';
            return;
        }

        this.contractMove.error = '';
        this.contractMove.lotErrors = {};
        this.contractMove.isSaving = true;

        this.wsListener.sendPut('admin.auctions.depositors.lotsContract', {
            auction: this.auctionId,
            depositor: target ? undefined : this.contractMove.newOwnerId,
            contract: target ? target.value : undefined,
            lots: lots.map((lot: any) => ({
                lot: lot.id,
                startingPrice: lot.startingPrice,
                estimateMin: lot.estimateMin,
                estimateMax: lot.estimateMax,
            })),
        });
    }

    get selectedSituatieLotsCount(): number {
        return (this.situatie.lots || []).filter((l: any) => l.selected).length;
    }

    private flattenLotForExport(lot: any, depositor: any): any {
        const sold = !!(lot.sold && lot.winnerPrice);
        const pct = this.effectiveCommissionPercent(lot, depositor);
        const fullCommission = pct >= 100;
        const adjudecatEur = this.lotAdjudecatPrice(lot);
        const confirmed = !!(
            lot.winnerAuctionUser && lot.winnerAuctionUser.paymentConfirmed
        );

        const author =
            lot.author && lot.author.name ? String(lot.author.name) : '';

        const contract = contractLabel(lot.depositorContract);

        let statusIncasare = '-';
        if (sold) {
            statusIncasare = confirmed ? 'Confirmată' : 'Neconfirmată';
        }

        return {
            id: String(lot._id || ''),
            selected: true,
            sold,
            confirmed,
            fullCommission,
            adjudecatEur,
            pct,
            position: lot.position || 0,
            author_name: author,
            name: lot.name ? String(lot.name) : '',
            contract,
            adjudecat: sold ? 'Da' : 'Nu',
            status_incasare: statusIncasare,
            thumbnail:
                lot.imagesData && lot.imagesData.length
                    ? lot.imagesData[0].thumbnail
                    : '',
        };
    }

    openSituatie(depositor: any) {
        if (!depositor || !depositor.depositor || !depositor.depositor._id) {
            return;
        }

        this.situatie.depositor = depositor;
        this.situatie.error = '';
        this.situatie.isGenerating = false;
        this.situatie.lots = (depositor.lots || []).map((lot: any) =>
            this.flattenLotForExport(lot, depositor),
        );
        this.situatie.isOpen = true;
    }

    private loadFile(url: string, callback: any) {
        PizZipUtils.getBinaryContent(url, callback);
    }

    async doGenerateSituatie(bvModalEvt?: BvModalEvent) {
        if (bvModalEvt) bvModalEvt.preventDefault();

        const depositor = this.situatie.depositor;
        const selected = (this.situatie.lots || []).filter(
            (l: any) => l.selected,
        );

        if (!depositor) return;
        if (!selected.length) {
            this.situatie.error = 'Selectează cel puțin un lot.';
            return;
        }

        this.situatie.error = '';
        this.situatie.isGenerating = true;

        const $this = this;
        const symbol = this.currencySymbol;

        this.loadFile('/static/situatie_deponent_v1.docx', function(
            error: any,
            content: any,
        ) {
            if (error) {
                $this.situatie.isGenerating = false;
                $this.situatie.error =
                    'Nu am putut încărca template-ul: ' +
                    (error.message || error);
                return;
            }

            try {
                const zip = new PizZip(content);
                const doc = new Docxtemplater(zip, {
                    paragraphLoop: true,
                    linebreaks: true,
                });

                let totalAdjudecat = 0;
                let totalCommission = 0;
                let totalNet = 0;

                const lots = selected.map((l: any) => {
                    const counts = l.sold && l.confirmed && !l.fullCommission;
                    const commissionEur = $this.getComissionAmount(
                        l.adjudecatEur,
                        l.pct,
                    );
                    const netEur = $this.getNetAmount(l.adjudecatEur, l.pct);

                    if (counts) {
                        totalAdjudecat += l.adjudecatEur;
                        totalCommission += commissionEur;
                        totalNet += netEur;
                    }

                    const fmt = (eur: number) =>
                        `${truncate2($this.formatPrice(eur))} ${symbol}`;

                    return {
                        position: l.position || 0,
                        author_name: l.author_name || '',
                        name: l.name || '',
                        contract: l.contract || '',
                        adjudecat: l.adjudecat,
                        status_incasare: l.status_incasare,
                        pret_adjudecare: l.sold ? fmt(l.adjudecatEur) : '-',
                        commission_pct: l.sold ? `${l.pct}%` : '-',
                        commission:
                            l.sold && l.confirmed && !l.fullCommission
                                ? fmt(commissionEur)
                                : '-',
                        net:
                            l.sold && l.confirmed && !l.fullCommission
                                ? fmt(netEur)
                                : '-',
                    };
                });

                const contractsLabel = $this.contractsLabel(depositor);

                const payload: any = {
                    date_today: moment().format('DD.MM.YYYY'),
                    ...depositorDocxData(depositor.depositor),
                    contracts_label: contractsLabel || '',
                    currency_symbol: symbol,
                    euro_rate: String($this.auction.euroRate || ''),
                    lots,
                    total_adjudecat: `${truncate2(
                        $this.formatPrice(totalAdjudecat),
                    )} ${symbol}`,
                    total_commission: `${truncate2(
                        $this.formatPrice(totalCommission),
                    )} ${symbol}`,
                    total_net: `${truncate2(
                        $this.formatPrice(totalNet),
                    )} ${symbol}`,
                };

                doc.setData(payload);
                doc.render();

                const out = doc.getZip().generate({
                    type: 'blob',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                });

                const safeName = safeFileNamePart(
                    payload.depositor_name,
                    'deponent',
                );
                const fileName =
                    'situatie-' + safeName + '-' + payload.date_today + '.docx';

                saveAs(out, fileName);

                $this.situatie.isGenerating = false;
                $this.situatie.isOpen = false;
            } catch (e) {
                console.error('Situatie render error:', e);
                $this.situatie.isGenerating = false;
                const err: any = e;
                $this.situatie.error =
                    'Eroare la generarea documentului: ' +
                    (err && err.message ? err.message : err);
            }
        });
    }

    /**
     * Snapshot of the adjudicated lots, grouped by depositor. Kept separate
     * from `data.auctionDepositors`, which is replaced wholesale on every
     * `admin.auctions.depositors` response - the selection has to survive that.
     */
    private buildExcelGroups(): any[] {
        const groups: any[] = [];

        (this.data.auctionDepositors || []).forEach((depositor: any) => {
            const details = (depositor && depositor.depositor) || {};
            const lots = (depositor.lots || [])
                .map((lot: any) => this.flattenLotForExport(lot, depositor))
                .filter((lot: any) => lot.sold);

            if (!lots.length) return;

            const name = `${details.firstName || ''} ${details.lastName ||
                ''}`.trim();

            groups.push({
                depositorId: String(details._id || ''),
                depositorName: name || '(deponent fără nume)',
                lots,
            });
        });

        return groups;
    }

    openExcelExport() {
        this.excelExport.groups = this.buildExcelGroups();
        this.excelExport.error = '';
        this.excelExport.stale = false;
        this.excelExport.isOpen = true;
    }

    get excelExportTotalCount(): number {
        return totalLotCount(this.excelExport.groups);
    }

    get excelExportSelectedCount(): number {
        return selectedLotCount(this.excelExport.groups);
    }

    get excelAllSelected(): boolean {
        const total = this.excelExportTotalCount;
        return total > 0 && this.excelExportSelectedCount === total;
    }

    get excelSomeSelected(): boolean {
        return this.excelExportSelectedCount > 0;
    }

    toggleExcelAll() {
        const next = !this.excelAllSelected;
        (this.excelExport.groups || []).forEach((group: any) =>
            setLotsSelected(group.lots, next),
        );
    }

    excelGroupSelectedCount(group: any): number {
        return groupSelectedCount(group);
    }

    excelGroupAllSelected(group: any): boolean {
        return isGroupFullySelected(group);
    }

    toggleExcelGroup(group: any) {
        setLotsSelected(group && group.lots, !this.excelGroupAllSelected(group));
    }

    doGenerateExcel(bvModalEvt?: BvModalEvent) {
        if (bvModalEvt) bvModalEvt.preventDefault();

        if (this.selectedCurrency !== 'EUR' && !this.auction.euroRate) {
            this.excelExport.error =
                'Licitația nu are curs BNR setat, exportul în Lei ar da valori zero.';
            return;
        }

        this.excelExport.error = '';

        const symbol = this.currencySymbol;
        const rows: XlsxCellInput[][] = [];

        rows.push([
            {
                value: `Deponenți — ${(this.auction && this.auction.name) || ''}`,
                variant: 'bold',
            },
        ]);
        rows.push([
            `Monedă: ${symbol}`,
            '',
            `Curs BNR: ${this.auction.euroRate || '-'}`,
            '',
            `Generat: ${moment().format('DD.MM.YYYY HH:mm')}`,
        ]);
        rows.push([]);
        rows.push([
            { value: 'Deponent', variant: 'header' },
            { value: 'Contract', variant: 'header' },
            { value: 'Lot', variant: 'header' },
            { value: 'Denumire', variant: 'header' },
            { value: `Preț adjudecare (${symbol})`, variant: 'header' },
        ]);

        // Accumulated in integer cents: summing truncated floats and
        // truncating again drifts by a cent, and the column has to add up to
        // the printed subtotal exactly.
        let grandCents = 0;
        let grandCount = 0;

        (this.excelExport.groups || []).forEach((group: any) => {
            const selected = (group.lots || []).filter((lot: any) => lot.selected);
            if (!selected.length) return;

            let subtotalCents = 0;

            selected.forEach((lot: any) => {
                const price = truncate2(this.formatPrice(lot.adjudecatEur));
                subtotalCents += Math.round(price * 100);

                rows.push([
                    group.depositorName,
                    lot.contract || '-',
                    lot.position || 0,
                    lot.author_name ? `${lot.author_name} - ${lot.name}` : lot.name,
                    { value: price, format: 'decimal' },
                ]);
            });

            rows.push([
                { value: `Subtotal ${group.depositorName}`, variant: 'bold' },
                { value: '', variant: 'bold' },
                { value: '', variant: 'bold' },
                { value: lotsLabel(selected.length), variant: 'bold' },
                { value: subtotalCents / 100, variant: 'bold', format: 'decimal' },
            ]);
            rows.push([]);

            grandCents += subtotalCents;
            grandCount += selected.length;
        });

        rows.push([
            { value: 'TOTAL GENERAL', variant: 'total' },
            { value: '', variant: 'total' },
            { value: '', variant: 'total' },
            { value: lotsLabel(grandCount), variant: 'total' },
            { value: grandCents / 100, variant: 'total', format: 'decimal' },
        ]);

        const sheet: XlsxSheet = {
            name: 'Deponenti',
            rows,
            freezeRows: 4,
            columns: [
                { width: 30 },
                { width: 18 },
                { width: 8 },
                { width: 55 },
                { width: 20 },
            ],
        };

        const safeName = safeFileNamePart(
            this.auction && this.auction.name,
            'licitatie',
        );

        saveXlsx(
            [sheet],
            'deponenti-' + safeName + '-' + moment().format('DD.MM.YYYY') + '.xlsx',
        );

        this.excelExport.isOpen = false;
    }

    /** Notele tuturor deponentilor, intr-o singura cerere. */
    loadDepositorNotes() {
        this.wsListener.sendGet('admin.auctions.depositors.notes', {
            auction: this.auctionId,
        });
    }

    addDepositorNote(depositorId: string) {
        const note = this.newNote[depositorId];
        if (!note || !note.description) return;

        this.$set(this.addingNote, depositorId, true);
        this.wsListener.sendPut('admin.auctions.depositors.notes', {
            auction: this.auctionId,
            depositor: depositorId,
            description: note.description,
        });
    }

    deleteDepositorNote(depositorId: string, noteId: string) {
        this.wsListener.sendDelete('admin.auctions.depositors.notes', {
            auction: this.auctionId,
            depositor: depositorId,
            noteId,
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/auction-manage/depositors-tab/index.ts